import { ApolloCache } from "@apollo/client"

import {
  ProjectParentTypename,
  ProjectTypename,
  isProjectParentTypename
} from "shared/types/Project"

import { findProjectChildParent } from "./findProjectChildParent"

export interface ResolvedProjectChildParent {
  id: string
  __typename: ProjectParentTypename
}

/**
 * Resolves the parent a project child hangs off, which is needed to invalidate
 * the parent's cached price after a mutation.
 *
 * The cache read behind `findProjectChildParent` is all-or-nothing: an evicted
 * ancestor or a not-yet-fetched subtree makes it return null even though the
 * child itself is fine. So a cold cache is refetched once before we give up and
 * attribute the child to the project, which is the correct parent for top-level
 * children and a safe coarser invalidation target for nested ones.
 */
export async function resolveProjectChildParent({
  projectID,
  childID,
  cache,
  refetch
}: {
  projectID: string
  childID: string
  cache: ApolloCache<object>
  refetch: () => Promise<unknown>
}): Promise<ResolvedProjectChildParent> {
  function read(): ResolvedProjectChildParent | null {
    const parent = findProjectChildParent({ projectID, childID, cache })

    if (parent && isProjectParentTypename(parent.__typename)) {
      return { id: parent.id, __typename: parent.__typename }
    }

    return null
  }

  const fromCache = read()
  if (fromCache) return fromCache

  await refetch()

  return read() ?? { id: projectID, __typename: ProjectTypename }
}
